/**
 * Time helpers.
 *
 * toDate() is the single place that knows about provider-specific date shapes.
 * Everything downstream deals only with JavaScript Date objects.
 */

/**
 * Coerce whatever a backend handed us into a Date, or null.
 *
 * Handles Firestore Timestamp ({seconds, nanoseconds} or .toDate()),
 * MySQL DATETIME strings, ISO strings, and epoch milliseconds.
 *
 * @param {*} value
 * @returns {Date|null}
 */
export function toDate(value) {
  if (value === null || value === undefined) return null
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value

  // Firestore Timestamp
  if (typeof value.toDate === 'function') {
    try { return value.toDate() } catch { return null }
  }
  if (typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000 + (value.nanoseconds ?? 0) / 1e6)
  }

  if (typeof value === 'number') return new Date(value)

  if (typeof value === 'string') {
    // MySQL hands back "2026-10-23 14:05:00", which Safari refuses to parse
    const normalised = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(value)
      ? value.replace(' ', 'T') + 'Z'
      : value
    const parsed = new Date(normalised)
    return isNaN(parsed.getTime()) ? null : parsed
  }

  return null
}

const MINUTE = 60_000
const HOUR   = 60 * MINUTE
const DAY    = 24 * HOUR

export function isToday(date, now = new Date()) {
  return date.toDateString() === now.toDateString()
}

export function isYesterday(date, now = new Date()) {
  const yesterday = new Date(now.getTime() - DAY)
  return date.toDateString() === yesterday.toDateString()
}

/** "14:05" — the timestamp under a message bubble. */
export function formatTime(date) {
  if (!date) return ''
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/** Chat-list timestamp: time today, "Yesterday", weekday this week, else a date. */
export function formatListTime(date, now = new Date()) {
  if (!date) return ''
  if (isToday(date, now)) return formatTime(date)
  if (isYesterday(date, now)) return 'Yesterday'
  if (now.getTime() - date.getTime() < 7 * DAY) {
    return date.toLocaleDateString([], { weekday: 'short' })
  }
  return date.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: '2-digit' })
}

/** The divider between days inside a conversation. */
export function formatDayDivider(date, now = new Date()) {
  if (!date) return ''
  if (isToday(date, now)) return 'Today'
  if (isYesterday(date, now)) return 'Yesterday'
  return date.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' })
}

/** "last seen today at 14:05" / "online" */
export function formatLastSeen(date, isOnline, now = new Date()) {
  if (isOnline) return 'online'
  if (!date) return ''
  if (isToday(date, now))     return `last seen today at ${formatTime(date)}`
  if (isYesterday(date, now)) return `last seen yesterday at ${formatTime(date)}`
  return `last seen ${date.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: '2-digit' })}`
}

/** "3:05" for a voice note or call duration. */
export function formatDuration(ms) {
  if (!ms || ms < 0) return '0:00'
  const total   = Math.floor(ms / 1000)
  const hours   = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  const pad = n => String(n).padStart(2, '0')
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`
}

/** Countdown for a status update: "7h left". */
export function formatExpiry(expiresAt, now = new Date()) {
  const remaining = expiresAt.getTime() - now.getTime()
  if (remaining <= 0) return 'expired'
  if (remaining < HOUR) return `${Math.ceil(remaining / MINUTE)}m left`
  return `${Math.floor(remaining / HOUR)}h left`
}

/** Do these two messages belong under the same day divider? */
export function isSameDay(a, b) {
  return Boolean(a && b) && a.toDateString() === b.toDateString()
}
